/**
 * drawingStore — 원본 도면 첨부용 IndexedDB 기반 key-value 저장소
 *
 * - putDrawings(items[]): 각 item.code → dataUrl 매핑 저장 (IndexedDB + 인메모리 캐시)
 * - getDrawing(code): Promise<dataUrl | null> (비동기 IndexedDB 조회)
 * - getSync(code): string | null (인메모리 동기 조회 — PDF 생성 시 사용)
 * - loadFromDB(): IndexedDB → 인메모리 캐시 복원 (세션 복구용, 브라우저 진입 시 호출)
 *
 * 브라우저 환경에서만 동작. SSR 시 no-op.
 */

const DB_NAME = 'furniture-drawing-store';
const DB_VERSION = 1;
const STORE_NAME = 'drawings';

// 인메모리 캐시 (동기 접근용)
const cache = new Map<string, string>();

let dbReady: Promise<IDBDatabase | null> | null = null;

function openDB(): Promise<IDBDatabase | null> {
  if (typeof window === 'undefined' || !window.indexedDB) return Promise.resolve(null);
  if (dbReady) return dbReady;

  dbReady = new Promise((resolve) => {
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      console.warn('[drawingStore] IndexedDB open failed, falling back to memory-only:', req.error);
      resolve(null);
    };
  });
  return dbReady;
}

/**
 * 여러 item의 원본 도면 dataURL을 저장한다.
 * 각 entry: { code: string, dataUrl: string }
 */
export async function putDrawings(entries: { code: string; dataUrl: string }[]): Promise<void> {
  for (const { code, dataUrl } of entries) {
    cache.set(code, dataUrl);
  }
  const db = await openDB();
  if (!db) return;
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const { code, dataUrl } of entries) {
      store.put(dataUrl, code);
    }
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('[drawingStore] IndexedDB write failed:', e);
  }
}

/**
 * 단일 item의 원본 도면 dataURL을 저장한다.
 */
export async function putDrawing(code: string, dataUrl: string): Promise<void> {
  cache.set(code, dataUrl);
  const db = await openDB();
  if (!db) return;
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(dataUrl, code);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('[drawingStore] IndexedDB write failed:', e);
  }
}

/**
 * 비동기 조회 — IndexedDB에서 읽고 인메모리 캐시도 갱신
 */
export async function getDrawing(code: string): Promise<string | null> {
  // 인메모리 캐시 먼저 확인
  if (cache.has(code)) return cache.get(code)!;

  const db = await openDB();
  if (!db) return null;
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(code);
    return new Promise<string | null>((resolve) => {
      req.onsuccess = () => {
        const val = req.result as string | undefined;
        if (val) cache.set(code, val);
        resolve(val || null);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/**
 * 동기 조회 — 인메모리 캐시에서 즉시 읽음 (PDF 생성 HTML 문자열 조립 시 사용)
 * dataURL이 미리 putDrawings()로 저장되어 있어야 조회 가능.
 */
export function getSync(code: string): string | null {
  return cache.get(code) || null;
}

/**
 * IndexedDB → 인메모리 캐시 전체 복원 (브라우저 세션 시작 시 호출)
 */
export async function loadFromDB(): Promise<void> {
  const db = await openDB();
  if (!db) return;
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAllKeys();
    return new Promise<void>((resolve) => {
      req.onsuccess = async () => {
        const keys = req.result as string[];
        for (const key of keys) {
          const valReq = store.get(key);
          await new Promise<void>((innerResolve) => {
            valReq.onsuccess = () => {
              if (valReq.result) cache.set(key, valReq.result as string);
              innerResolve();
            };
            valReq.onerror = () => innerResolve();
          });
        }
        resolve();
      };
      req.onerror = () => resolve();
    });
  } catch {
    // silent fail
  }
}

/**
 * 특정 code의 도면 삭제
 */
export async function removeDrawing(code: string): Promise<void> {
  cache.delete(code);
  const db = await openDB();
  if (!db) return;
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(code);
    await new Promise<void>((resolve) => { tx.oncomplete = () => resolve(); });
  } catch { /* ignore */ }
}

/**
 * 전체 초기화
 */
export async function clearAll(): Promise<void> {
  cache.clear();
  const db = await openDB();
  if (!db) return;
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    await new Promise<void>((resolve) => { tx.oncomplete = () => resolve(); });
  } catch { /* ignore */ }
}
